import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
// QA-149 (Kelly, 2026-08-19): the header's Capture icon is replaced with the
// new viewfinder-frame design (see media Screenshot_2026-08-19_at_4.19.40_PM.
// png), recreated as line-art SVG in assets/icons/CaptureFrame.jsx. This
// supersedes the QA-57 EditNoteOutlined substitute below.
import CaptureFrame from "../../../assets/icons/CaptureFrame";
import { PATH } from "../../../utils/config";
import { colors } from "../../../utils";
import { CAPTURE_ENABLED } from "../../../constants/featureFlags";
import { IconWrapper } from "./styles";
import { NavEntryMenuItem } from "./NavEntryMenuItem";
import { isSectionActive } from "./headerNavActive";

/**
 * Capture / Notepad inbox header nav entry (WL-021 / ISP-4018, fixes ISP-4068 B
 * "no entry point"). The first-class, discoverable header affordance that makes
 * the built-but-dark Capture surface (/capture) REACHABLE.
 *
 * Self-gates on CAPTURE_ENABLED (default OFF) exactly like CapturedOnWheel.jsx:
 * while the flag is off it renders null, so the header is byte-identical to a
 * build without Capture. Staging sets REACT_APP_CAPTURE_ENABLED=true
 * (DevOps-Sandbox.yml) so the entry is live there. Extracted from the inline
 * GalleryHeader block so the nav path is an independently-testable shared
 * component (keeps the rendered DOM identical: same Tooltip + IconWrapper,
 * same a11y contract).
 *
 * The route itself is gated by the SAME flag in routes.js; the backend
 * CAPTURE_ENABLED Django env must ALSO be on or /capture/items/ 404s.
 */
const CaptureNavEntry = ({ variant = "icon", ...rest }) => {
  const nav = useNavigate();
  const { pathname } = useLocation();

  if (!CAPTURE_ENABLED) return null;

  const go = () => nav(PATH.CAPTURE);
  const active = isSectionActive(pathname, PATH.CAPTURE);

  // Phone fold (see styles.js SecondaryActions): the SAME flag, label and
  // action rendered as a labeled overflow-menu row instead of a bare icon, so
  // the two presentations can never drift apart.
  if (variant === "menu") {
    return (
      <NavEntryMenuItem
        label="Capture"
        onActivate={go}
        icon={<CaptureFrame color={colors.PrimaryPurple} width={22} height={22} />}
        {...rest}
      />
    );
  }

  return (
    <Tooltip title="Capture">
      <IconWrapper
        className="capture-button"
        role="button"
        tabIndex={0}
        aria-label="Capture"
        aria-current={active ? "page" : undefined}
        isActive={active}
        onClick={go}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            go();
          }
        }}
        style={{ cursor: "pointer" }}
      >
        <CaptureFrame color={colors.PrimaryPurple} width={26} height={26} />
      </IconWrapper>
    </Tooltip>
  );
};

export default CaptureNavEntry;
